import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import { useAuth0 } from "contexts/AuthManager/Auth";
import useWindowSize from "utils/customHooks/useWindowSize";
import { CustomJsonEditor } from "components/Nav/Drawers/RightDrawer/FlightDebugger/debuggerComponents/CustomJsonEditor";
import SearchIcon from "@mui/icons-material/Search";

/**
 * Component to display and manage users with access to Sight Analytics
 */
export const SightUsersSection: React.FC = () => {
  const { translate } = useFlightTranslation();
  const { user } = useAuth0(); // Get current user from Auth0
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<string[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Add pagination and search state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);

        try {
          // Flag key for the Sight Analytics access
          const flagKey = "flight-link-to-sight-analytics";

          // Make a direct call to the LaunchDarkly API using the API key from env variables
          // LaunchDarkly requires the Authorization header to be in this specific format
          const apiKey = process.env.REACT_APP_LAUNCHDARKLY_API_KEY || "";

          const response = await fetch(
            `https://app.launchdarkly.com/api/v2/flags/default/${flagKey}`,
            {
              headers: {
                Authorization: apiKey.startsWith("api-")
                  ? apiKey
                  : `api-key ${apiKey}`,
              },
            }
          );

          if (!response.ok) {
            throw new Error(
              `API error: ${response.status} - ${response.statusText}`
            );
          }

          const flagData = await response.json();

          // If we got here, the API call was successful

          // Process the targeting rules to extract users
          // The structure is: flagData.environments.production.rules
          const extractedUsers: string[] = [];

          if (flagData?.environments?.production?.rules) {
            const rules = flagData.environments.production.rules;

            // Look for rules that serve the "enabled" variation
            rules.forEach((rule: any) => {
              if (rule.variation === 1 || rule.variation === "enabled") {
                // Assuming 0 is the index for "enabled"
                // Extract users from clauses that target specific users
                rule.clauses.forEach((clause: any) => {
                  if (
                    clause.attribute === "email" ||
                    clause.attribute === "user"
                  ) {
                    clause.values.forEach((email: string) => {
                      extractedUsers.push(email);
                    });
                  }
                });
              }
            });
          }

          // Add any users from individual targets
          if (flagData?.environments?.production?.targets) {
            const targets = flagData.environments.production.targets;
            // Find targets for the "enabled" variation (often 0 or "true")
            const enabledTargets = targets.find(
              (t: any) => t.variation === 0 || t.variation === "enabled"
            );

            if (enabledTargets && enabledTargets.values) {
              enabledTargets.values.forEach((email: string) => {
                // Check if this user is already in our list
                if (!extractedUsers.includes(email)) {
                  extractedUsers.push(email);
                }
              });
            }
          }

          // If we found users, use them
          if (extractedUsers.length > 0) {
            setUsers(extractedUsers);
            // Initialize filtered users with all users to prevent empty display
            // (the useEffect will handle proper filtering once the state updates)
            setFilteredUsers(extractedUsers);
          } else {
            // No users found, might be due to API structure differences
            setNote(
              "No users found in the targeting rules for the enabled variation. This might be due to how the flag is configured."
            );

            // Fallback to some sample users for demo purposes
            const fallbackUsers: string[] = [
              "no.users.found@example.com",
              "check.flag.configuration@example.com",
              "user@raventelemetry.com",
              "test@example.com",
              "admin@example.org",
            ];
            setUsers(fallbackUsers);
            // Initialize filtered users with fallback users
            setFilteredUsers(fallbackUsers);
          }
        } catch (apiError: any) {
          console.error("Error accessing LaunchDarkly API:", apiError);

          // Provide detailed error information
          const errorMessage =
            apiError.message || "Failed to connect to LaunchDarkly API";

          setNote(
            `Error: ${errorMessage}. This may be due to API key permissions or network issues.`
          );

          // Fallback to sample users
          const fallbackUsers: string[] = [
            "api.error@example.com",
            "check.console.for.details@example.com",
          ];
          setUsers(fallbackUsers);
          // Initialize filtered users with fallback users
          setFilteredUsers(fallbackUsers);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching sight users:", err);
        setError("Failed to load users from LaunchDarkly flag rules");
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on the selected domain or the current user's email domain
  useEffect(() => {
    if (!loading && users.length > 0 && user?.email) {
      // Extract the domain from the current user's email
      const currentUserEmail = user.email.toLowerCase();
      const currentUserDomain = currentUserEmail.split("@")[1];

      // always filter by their own domain
      const usersWithSameDomain = users.filter(email => {
        const emailLower = email.toLowerCase();
        return emailLower.endsWith(`@${currentUserDomain}`);
      });

      setFilteredUsers(usersWithSameDomain);
      setNote(
        `Showing only users with the same email domain as you (${currentUserDomain}). ${usersWithSameDomain.length} of ${users.length} total users match this filter.`
      );
    } else if (!loading && users.length > 0) {
      // If no user email is available, show all users with a note
      setFilteredUsers(users);
      setNote(
        `Showing all ${users.length} users. No filtering applied (user email not available).`
      );
    }
  }, [loading, users, user?.email]);

  // Add a function to handle page changes
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Add a function to handle rows per page changes
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Add a function to handle search
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  // Filter users based on search term
  const displayedUsers = filteredUsers.filter(email =>
    email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = displayedUsers.slice(startIndex, endIndex);

  // Prepare data in a more structured format for the Sight users editor
  const sightUserData = {
    sightUsers: filteredUsers,
    count: filteredUsers.length,
    totalUsersBeforeFiltering: users.length,
    lastUpdated: new Date().toISOString(),
    description: "Users with access to Sight Analytics",
  };

  // No longer needed - Flight Users section has been removed

  const { innerHeight } = useWindowSize();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography color="error">{error}</Typography>
        <Button
          variant="outlined"
          color="primary"
          sx={{ mt: 2 }}
          onClick={() => window.location.reload()}
        >
          {translate("Retry")}
        </Button>
      </Paper>
    );
  }

  return (
    <>
      {/* Sight Users Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5">{translate("Sight Users")}</Typography>
        <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
          {translate("The following users have access to Sight Analytics.")}
        </Typography>

        {note && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {note}
          </Alert>
        )}

        {/* Add search field */}
        <Box
          sx={{ p: 2, bgcolor: "#f9f9f9", borderBottom: "1px solid #e0e0e0" }}
        >
          <TextField
            fullWidth
            placeholder={translate("Search emails...")}
            value={searchTerm}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#f5f5f5",
                    borderBottom: "2px solid #ddd",
                  }}
                >
                  {translate("Email")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((email, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                      "&:hover": { backgroundColor: "#f0f0f0" },
                    }}
                  >
                    <TableCell
                      sx={{
                        padding: "12px",
                        userSelect: "text", // Explicitly enable text selection
                        cursor: "text", // Show text cursor on hover
                      }}
                    >
                      {email}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={1} align="center" sx={{ py: 3 }}>
                    {searchTerm ? "No matching emails found" : "No users found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add pagination */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={displayedUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {process.env.NODE_ENV === "development" && (
          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              * JSON viewer below is only visible in development mode
            </Typography>
            <CustomJsonEditor
              key={JSON.stringify(sightUserData)}
              value={sightUserData}
              height={(innerHeight - 350) / 2}
            />
          </Box>
        )}
      </Paper>
    </>
  );
};
